// Perplexity Sonar-backed autocomplete for the medical free-text intake
// questions (conditions / medications / allergies). Patients often can't
// spell terms like "levothyroxine" or "diverticulitis"; we suggest
// correctly-spelled completions for the term they're currently typing.
//
// Privacy: only the current partial term (e.g. "metfor") ever leaves the
// browser — never the patient's full answer or any identifying data.
//
// Prod path: POST ${REACT_APP_BACKEND_URL}/ai/medical-autocomplete (BE
// holds the Perplexity key — see docs/BACKEND_MEDICAL_AUTOCOMPLETE_PRD.md).
// Local-dev path: set REACT_APP_PERPLEXITY_API_KEY to call Sonar directly.
// Never set that var in Vercel — it would ship the key in the bundle.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEV_PERPLEXITY_KEY = process.env.REACT_APP_PERPLEXITY_API_KEY;
const PERPLEXITY_URL = 'https://api.perplexity.ai/chat/completions';

export const MIN_TERM_LENGTH = 3;
export const MAX_SUGGESTIONS = 5;

// The three questions from the intake flows plus their follow-up
// (prefilled) counterparts. Keyed by question id — ids are consistent
// across all visit-type data files.
const DOMAIN_BY_QUESTION_ID = {
  'medical-conditions': 'conditions',
  'current-medical-condition': 'conditions',
  'medications-list': 'medications',
  'self-reported-meds': 'medications',
  allergies: 'allergies',
};

const SYSTEM_PROMPTS = {
  conditions:
    'You are an autocomplete engine for a telehealth intake form. The patient is typing one item in a list of their medical conditions (chronic conditions, past surgeries, or other health issues). Given their partial input, return the most likely correctly-spelled completions, phrased the way a patient would list them (plain-language qualifier in parentheses when helpful, e.g. "Hypertension (high blood pressure)").',
  medications:
    'You are an autocomplete engine for a telehealth intake form. The patient is typing one item in a list of medications they currently take (prescription drugs, over-the-counter drugs, or supplements). Given their partial input, return the most likely correctly-spelled medication or supplement names. Name only — do not invent dosages.',
  allergies:
    'You are an autocomplete engine for a telehealth intake form. The patient is typing one item in a list of their known allergies (drug, food, or environmental). Given their partial input, return the most likely correctly-spelled allergen names.',
};

export const getAutocompleteDomain = (questionId) =>
  DOMAIN_BY_QUESTION_ID[questionId] || null;

// Ships dark: on only via env flag (BE proxy live) or a local dev key.
export const isAutocompleteEnabled = () =>
  process.env.REACT_APP_MED_AUTOCOMPLETE_ENABLED === 'true' ||
  Boolean(DEV_PERPLEXITY_KEY);

// The textarea holds a free-text list; suggestions apply to the term the
// patient is typing right now — everything after the last separator.
export const extractCurrentTerm = (text) => {
  const s = text || '';
  const sep = Math.max(
    s.lastIndexOf(','),
    s.lastIndexOf(';'),
    s.lastIndexOf('\n')
  );
  const raw = s.slice(sep + 1);
  const term = raw.replace(/^\s+/, '');
  return { term, start: sep + 1 + (raw.length - term.length) };
};

export const applySuggestion = (text, suggestion) => {
  const s = text || '';
  const { start } = extractCurrentTerm(s);
  const prefix = s.slice(0, start);
  return (start > 0 && !/\s$/.test(prefix) ? `${prefix} ` : prefix) + suggestion;
};

const parseSuggestions = (payload) => {
  let list = payload;
  if (typeof payload === 'string') {
    try {
      list = JSON.parse(payload);
    } catch {
      return [];
    }
  }
  if (list && !Array.isArray(list)) list = list.suggestions;
  if (!Array.isArray(list)) return [];
  return list
    .filter((item) => typeof item === 'string' && item.trim())
    .map((item) => item.trim())
    .slice(0, MAX_SUGGESTIONS);
};

const cache = new Map();
const CACHE_MAX = 200;

const cacheGet = (key) => cache.get(key);
const cacheSet = (key, value) => {
  if (cache.size >= CACHE_MAX) {
    cache.delete(cache.keys().next().value);
  }
  cache.set(key, value);
};

const fetchDirectFromSonar = async (domain, term, signal) => {
  const response = await fetch(PERPLEXITY_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${DEV_PERPLEXITY_KEY}`,
    },
    signal,
    body: JSON.stringify({
      model: 'sonar',
      messages: [
        { role: 'system', content: SYSTEM_PROMPTS[domain] },
        { role: 'user', content: term },
      ],
      max_tokens: 200,
      temperature: 0,
      disable_search: true,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'autocomplete_suggestions',
          schema: {
            type: 'object',
            properties: {
              suggestions: {
                type: 'array',
                items: { type: 'string' },
                maxItems: MAX_SUGGESTIONS,
              },
            },
            required: ['suggestions'],
          },
        },
      },
    }),
  });
  if (!response.ok) throw new Error(`Perplexity ${response.status}`);
  const data = await response.json();
  return parseSuggestions(data?.choices?.[0]?.message?.content);
};

const fetchFromBackend = async (domain, term, signal) => {
  const response = await fetch(`${BACKEND_URL}/ai/medical-autocomplete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    signal,
    body: JSON.stringify({ domain, term }),
  });
  if (!response.ok) throw new Error(`Autocomplete ${response.status}`);
  const data = await response.json();
  return parseSuggestions(data?.suggestions);
};

// Resolves to [] on any failure — autocomplete must never block the intake.
export const fetchMedicalSuggestions = async (domain, term, signal) => {
  const normalized = (term || '').trim().toLowerCase();
  if (!SYSTEM_PROMPTS[domain] || normalized.length < MIN_TERM_LENGTH) return [];

  const cacheKey = `${domain}:${normalized}`;
  const cached = cacheGet(cacheKey);
  if (cached) return cached;

  try {
    const suggestions = DEV_PERPLEXITY_KEY
      ? await fetchDirectFromSonar(domain, term.trim(), signal)
      : await fetchFromBackend(domain, term.trim(), signal);
    cacheSet(cacheKey, suggestions);
    return suggestions;
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    return [];
  }
};
